// src/components/marketing/arte-contratacao/arte-layout.js
// =======================================================================
// A DIAGRAMAÇÃO da arte de contratação — coordenadas e quebra de texto.
// =======================================================================
// ⚠️⚠️ TUDO AQUI É COORDENADA FIXA NA IMAGEM DE FUNDO
//    (`public/marketing/arte-contratacao.jpg`, 3375×6000 — um Stories de
//    1080×1920 em 3,125×). Os rótulos "Vagas", "Contato" e "Endereço" já vêm
//    IMPRESSOS na arte; o que se desenha é só o VALOR de cada um, logo abaixo do
//    rótulo. **Trocar a arte de fundo quebra todas estas medidas de uma vez** —
//    e é justamente isso que o Marketing vai querer fazer um dia. Quando for,
//    é trabalho de programador: recalibrar este arquivo.
//
// ⚠️⚠️ O TAMANHO DE FONTE É ÚNICO (78) PARA OS TRÊS VALORES, e não é escolha
//    estética: 78 é o MAIOR tamanho em que o endereço mais longo cadastrado
//    (GTV, 74 caracteres) ainda cabe em 2 linhas. Em 92, aquele endereço pede 3
//    linhas e "Guarapari/ES" some cortado.
//
// ⚠️ O texto não pode passar da linha pontilhada vertical que separa o bloco do
//    QR Code (x = 2136 na arte). O limite usado é 2080, com folga.
// =======================================================================

export const ARTE = { W: 3375, H: 6000 };

export const LEFT_X = 560;
export const RIGHT_LIMIT = 2080;
export const MAXW = RIGHT_LIMIT - LEFT_X;

export const FONTE_TAMANHO = 78;
export const FONTE_ALTURA_LINHA = Math.round(FONTE_TAMANHO * 1.15);
export const FONTE = `400 ${FONTE_TAMANHO}px 'Space Grotesk', sans-serif`;

// Linha de base do rótulo já impresso + até onde o valor pode descer antes de
// cruzar a linha pontilhada (ou a borda do cartão, na última).
export const LINHA_VAGAS = { baseRotulo: 4265, limite: 4822 };
export const LINHA_CONTATO = { baseRotulo: 5030, limite: 5272 };
export const LINHA_ENDERECO = { baseRotulo: 5500, limite: 5796 };

const GAP_DEPOIS_DO_ROTULO = 28;   // "colado": só um respiro abaixo do rótulo
const MARGEM_SEGURA = 15;          // folga para descendentes ("ç", "p", "g")

/**
 * Quantas linhas cabem entre o rótulo e o limite, no tamanho único de fonte.
 * Conta a posição REAL da linha de base de cada linha — não uma divisão
 * grosseira de altura.
 */
export function maxLinhasDe(linha) {
    const topo = linha.baseRotulo + GAP_DEPOIS_DO_ROTULO;
    const primeiraBase = topo + FONTE_TAMANHO * 0.86;
    const fundoSeguro = linha.limite - MARGEM_SEGURA;
    const extras = Math.floor((fundoSeguro - primeiraBase) / FONTE_ALTURA_LINHA);
    return Math.max(1, 1 + extras);
}

/** O topo onde o valor começa a ser escrito, dado o rótulo. */
export function topoDoValor(linha) {
    return linha.baseRotulo + GAP_DEPOIS_DO_ROTULO;
}

/**
 * Quebra o texto em linhas que caibam em `larguraMax`.
 * @param {(texto:string)=>number} medir  largura do texto na fonte em uso
 */
export function quebrarEmLinhas(medir, texto, larguraMax) {
    const palavras = String(texto).split(/\s+/).filter(Boolean);
    const linhas = [];
    let atual = '';
    for (const palavra of palavras) {
        const teste = atual ? `${atual} ${palavra}` : palavra;
        if (medir(teste) > larguraMax && atual) {
            linhas.push(atual);
            atual = palavra;
        } else {
            atual = teste;
        }
    }
    if (atual) linhas.push(atual);
    return linhas;
}

/**
 * Põe reticências na última linha visível quando sobrou conteúdo.
 *
 * ⚠️ Ela entra MESMO que a linha, sozinha, ainda coubesse na largura: sem isso,
 *    o corte fica invisível — a cidade some do fim do endereço e nada avisa.
 */
export function comReticencias(medir, linha, larguraMax) {
    const candidata = `${linha.replace(/[\s,.;•–-]+$/, '')}…`;
    if (medir(candidata) <= larguraMax) return candidata;
    let corte = linha;
    while (corte.length > 1 && medir(`${corte}…`) > larguraMax) {
        corte = corte.slice(0, -1);
    }
    return `${corte.replace(/[\s,.;•–-]+$/, '')}…`;
}

/**
 * As linhas que realmente serão desenhadas: quebradas, limitadas ao que cabe e
 * com reticências quando houve corte.
 */
export function linhasVisiveis(medir, texto, larguraMax, maxLinhas) {
    const linhas = quebrarEmLinhas(medir, texto, larguraMax);
    if (linhas.length <= maxLinhas) return linhas;
    const cortadas = linhas.slice(0, maxLinhas);
    cortadas[maxLinhas - 1] = comReticencias(medir, cortadas[maxLinhas - 1], larguraMax);
    return cortadas;
}

/** Máscara de telefone brasileiro: (27) 99999-9999. */
export function mascaraTelefone(valor) {
    const digitos = String(valor || '').replace(/\D/g, '').slice(0, 11);
    if (digitos.length > 10) return digitos.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    if (digitos.length > 5) return digitos.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    if (digitos.length > 2) return digitos.replace(/(\d{2})(\d{0,5})/, '($1) $2');
    if (digitos.length > 0) return digitos.replace(/(\d{0,2})/, '($1');
    return '';
}
